import { useEffect, useRef, useState } from 'react'
import style from './Chat.module.css'
import ChatInput from './ChatInput'
import ChatInterface from './ChatInterface'
import useDraggable from '../../../hooks/useDraggable'
import { useFilesContext } from '../../../contexts/FilesContext'
import DropZoneIcon from '../../../assets/svgs/DropIcon'
import { useHistoryContext } from '../../../contexts/HistoryContext'
import { logEvent } from '../../../utils/logEvent'
import Cookies from 'js-cookie'
import FetchData from '../../../utils/FetchData'

const token = Cookies.get('token')

const Chat = ({ styles }) => {
  // formData remains only for the main message input (no changes to existing behavior)
  const [formData, setFormData] = useState({ message: '', cardUrl: '' })
  const [feedFaq, setFeedFaq] = useState([])
  const [openUpload, setOpenUpload] = useState(false) // reused for opening the Tools dropdown
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // Tools state
  const [activeTool, setActiveTool] = useState(null) // 'jobFitCheck' | null
  const [jobFitUrl, setJobFitUrl] = useState('')     // input when Job Fit Check is enabled and no feed items

  // Context
  const { addRequest, addResponse, history, updateLastResponse, persistLastResponse } =
    useHistoryContext()
  const { isDragging } = useDraggable()
  const { addFile, files, setFiles } = useFilesContext()

  // Ensure we only trigger the auto "Hi" once per mount when history is empty
  const introSeededRef = useRef(false)

  useEffect(() => {
    const fetchFeedFaq = async (categories) => {
      const data = await FetchData.feedFaq(categories)
      setFeedFaq(data)
    }

    const fileCategories = [...new Set(files.map((file) => file.category))]
    fetchFeedFaq(fileCategories)
  }, [files])

  // Derived booleans for tool UX
  const hasFiles = files.length > 0

  // Auto-disable Job Fit Check if ANY item is present
  useEffect(() => {
    if (activeTool === 'jobFitCheck' && hasFiles) {
      setActiveTool(null)
      logEvent({ action: 'tool_auto_disabled', content: 'Job Fit Check (any item attached)' })
    }
  }, [activeTool, hasFiles])

  // When a draggable card is dropped, update formData with its URL and enforce tool rules
  const handleDrop = (e) => {
    e.preventDefault()

    // Block adding items if user typed URL in Job Fit field
    if (jobFitUrl.trim().length > 0) {
      logEvent({ action: 'drop_blocked', content: 'Job Fit URL present' })
      return
    }
    console.log('types:', [...e.dataTransfer.types]);
    const data = e.dataTransfer.getData('application/json')
    if (data) {
      const item = JSON.parse(data)
      // Update hidden cardUrl (keeps existing behavior intact)
      setFormData((prev) => ({ ...prev, cardUrl: item.url }))
      addFile(item)

      logEvent({
        action: `drag_${item.category}`,
        content: item.title,
      })
    }
  }

  const handleDragOver = (e) => {
    // If URL typed, visually indicate we won't accept drops
    if (jobFitUrl.trim().length > 0) {
      e.dataTransfer.dropEffect = 'none'
    }
    e.preventDefault()
  }

  // Sending message to backend with added fields jobfiturl & itemcategory
  // opts.silentUser = true => do NOT show the user's message (used for auto "Hi")
  async function handleResponses(message, opts = {}) {
    const { silentUser = false } = opts

    if (!message) {
      setErrorMessage('Oops! Looks like you forgot to type your message')
      return
    }

    setErrorMessage('')
    setLoading(true)
    if (!silentUser) {
      addRequest(message) // visible user message only when not silent
    }

    const items = files.map(({ id, url, category }) => ({ id, url, category }))

    // Parallel arrays, if you want them separately as well
    const filesUrls = items.map(it => it.url)
    const itemIds = items.map(it => it.id)
    const itemCategories = items.map(it => it.category)

    // Determine jobfiturl to send (only if Job Fit Check is enabled)
    const jobfiturl = activeTool === 'jobFitCheck' ? (jobFitUrl || '') : ''

    // Clear local files (keeps your existing behavior)
    setFiles([])

    try {
      const payload = { message, urls: filesUrls, jobfiturl, itemIds, itemCategories }

      // Pretty + raw logs (mask token in logs)
      console.log('[POST] /chat headers:', {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token ? token.slice(0,6) + '…' : ''}`,
      });
      console.log('[POST] /chat payload (object):', payload);
      console.log('[POST] /chat payload (json):', JSON.stringify(payload, null, 2));

      const response = await fetch('https://chimpknows.com/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok || !response.body) throw new Error('Stream failed')

      const reader = response.body.getReader()
      const decoder = new TextDecoder('utf-8')

      let buffer = ''
      let fullText = ''

      setLoading(false)
      // Start a new assistant message to stream into
      addResponse('', undefined, { persist: false })

      while (true) {
        const { value, done } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop()

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const json = JSON.parse(line.slice(6).trim())
              if (json.content) {
                fullText += json.content
                updateLastResponse(fullText)
              }
            } catch (err) {
              // ignore malformed SSE chunks
            }
          }
        }
      }
      persistLastResponse()
    } catch (error) {
      console.error('Error streaming response:', error)
      setLoading(false)
      persistLastResponse()
    }
  }

  // Auto-start the conversation with "Hi" on a brand-new chat (once per mount),
  // without showing the user's "Hi" message in the UI.
  useEffect(() => {
    if (history.length === 0 && !introSeededRef.current) {
      introSeededRef.current = true
      handleResponses('Hi', { silentUser: true })
    }
    // We intentionally only depend on history length here to avoid re-firing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.length])

  // Pill UI clear
  const clearJobFitTool = () => {
    setActiveTool(null)
    setJobFitUrl('')
    logEvent({ action: 'tool_disabled', content: 'Job Fit Check (pill x)' })
  }

  return (
    <div
      className={style.container}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      style={{ ...styles }}
    >
      <p className={style.title}>Chimp</p>

      {history.length > 0 ? (
        <ChatInterface
          smallInterface={true}
          history={history}
          loading={loading}
          handleResponses={handleResponses}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
        />
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
          }}
        >
          {!isDragging ? (
            <div className={style.cardsContainer}>
              {feedFaq.map((faq, index) => (
                <div
                  className={style.card}
                  key={index}
                  onClick={() => handleResponses(faq.question)}
                >
                  <p className={style.cardTitle}>{faq.question}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className={style.dropZone}>
              <DropZoneIcon />
              <p className={style.dropZoneText}>
                Drag & drop items from dashboard to discuss with Chimp
              </p>
            </div>
          )}
        </div>
      )}

      {/* CHAT INPUT (Plus icon opens the Tools dropdown) */}
      <ChatInput
        setOpenUpload={setOpenUpload}
        openUpload={openUpload}
        handleResponses={handleResponses}
        setFormData={setFormData}
        formData={formData}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        // Tool props
        activeTool={activeTool}
        setActiveTool={setActiveTool}
        jobToolDisabled={hasFiles}  // disable tool if ANY item attached
      />

      {/* TOOL PILL + JOB URL INPUT (rendered BELOW the message input) */}
      <div style={{ marginTop: 8, marginBottom: 8 }}>
        {activeTool === 'jobFitCheck' && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              borderRadius: 999,
              border: '1px solid var(--border, #e5e7eb)',
              background: 'transparent',
              marginBottom: 8,
              boxShadow: '0 8px 28px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.18)', // stronger shadow
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 600 }}>Job Fit Check</span>
            <button
              aria-label="Disable Job Fit Check"
              onClick={clearJobFitTool}
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 14,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Show URL input IFF Job Fit Check is active AND no feed item is attached */}
        {activeTool === 'jobFitCheck' && !hasFiles && (
          <div style={{ marginTop: 8 }}>
            <input
              placeholder="enter job url"
              value={jobFitUrl}
              onChange={(e) => setJobFitUrl(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid var(--border, #e5e7eb)',
                outline: 'none',
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default Chat
